import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Card, Button, Field, Input, SectionTitle } from '../../components/ui';
import { Icon } from '../../components/icons';
import { BRAND, BRAND_LIGHT, getThemeColors, DENSITY_VALUES } from '../../lib/theme';
import { useIsMobile } from '../../lib/useBreakpoint';
import { MfaMethodsManager } from '../../components/auth/MfaMethodsManager';
import { NotificationSettings } from './NotificationSettings';
import { PasswordChangeForm } from './PasswordChangeForm';
import type { Density } from '../../types';

interface EinstellungenPageProps {
  dark: boolean;
  setDark: (v: boolean) => void;
  density: Density;
  setDensity: (v: Density) => void;
}

// Systemweite Sektionen (E-Mail-Vorlagen, PB/BK Stages, Kalender, Rollen &
// Rechte, Organisation) sind in die AdminEinstellungenPage umgezogen (WP6).
type Section = 'allgemein' | 'darstellung' | 'benachrichtigungen' | 'sicherheit';

const SECTIONS: { k: Section; labelKey: string; ic: keyof typeof Icon }[] = [
  { k: 'allgemein',          labelKey: 'settings.nav.general',       ic: 'Settings' },
  { k: 'darstellung',        labelKey: 'settings.nav.appearance',    ic: 'Sun' },
  { k: 'benachrichtigungen', labelKey: 'settings.nav.notifications', ic: 'Bell' },
  { k: 'sicherheit',         labelKey: 'settings.nav.security',      ic: 'Heart' },
];

function selectStyle(dark: boolean, t: ReturnType<typeof getThemeColors>): React.CSSProperties {
  return {
    width: '100%', border: `1px solid ${t.border}`, borderRadius: 9,
    padding: '9px 12px', fontSize: 13, color: t.heading, background: t.inputBg,
    fontFamily: 'Outfit, sans-serif', cursor: 'pointer', outline: 'none',
  };
}

export function EinstellungenPage({ dark, setDark, density, setDensity }: EinstellungenPageProps) {
  const t = getThemeColors(dark);
  const { t: tr, i18n } = useTranslation();
  const d = DENSITY_VALUES[density];
  const isMobile = useIsMobile();
  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng); // i18next-Detector cached automatisch nach localStorage 'sh24.locale'
    document.documentElement.lang = lng;
  };
  const [section, setSection] = useState<Section>('allgemein');

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '240px 1fr',
      gap: isMobile ? 12 : d.gap, maxWidth: 1100,
    }}>
      {/* Sidebar nav (Desktop) / horizontale Tab-Leiste (Mobile, sticky an der Navbar) */}
      <Card
        dark={dark}
        style={isMobile ? {
          padding: 4,
          display: 'flex', flexDirection: 'row', gap: 6,
          overflowX: 'auto', WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'thin',
          position: 'sticky', top: 0, zIndex: 10,
        } : { padding: 12 }}
      >
        {SECTIONS.map(s => {
          const Ic = Icon[s.ic];
          const active = section === s.k;
          return (
            <button
              key={s.k}
              onClick={() => setSection(s.k)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                width: isMobile ? 'auto' : '100%',
                padding: isMobile ? '9px 14px' : '9px 11px',
                borderRadius: 8, border: 'none',
                background: active ? BRAND_LIGHT : 'transparent',
                color: active ? BRAND : t.text,
                fontSize: 13, fontWeight: active ? 600 : 500, cursor: 'pointer',
                marginBottom: isMobile ? 0 : 2,
                flexShrink: 0, whiteSpace: 'nowrap',
                minHeight: isMobile ? 38 : undefined,
                textAlign: 'left', fontFamily: 'Outfit, sans-serif',
                transition: 'background 0.12s, color 0.12s',
              }}
            >
              <Ic c={active ? BRAND : t.muted} />{tr(s.labelKey)}
            </button>
          );
        })}
      </Card>

      {/* Content */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: d.gap }}>

        {/* Allgemein — nur noch persönliche Sprache; Firmenname/Zeitzone/
            Währung sind in die Admin Einstellungen (Organisation) umgezogen */}
        {section === 'allgemein' && (
          <Card dark={dark} style={{ padding: d.cardPad }}>
            <SectionTitle dark={dark} title={tr('settings.general.personal_title')} subtitle={tr('settings.general.personal_subtitle')} />
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
              <Field label={tr('settings.general.language')}>
                <select value={(i18n.language || 'de').slice(0, 2)} onChange={e => changeLanguage(e.target.value)} style={selectStyle(dark, t)}>
                  <option value="de">Deutsch</option>
                  <option value="en">English</option>
                  <option value="pl">Polski</option>
                </select>
              </Field>
            </div>
          </Card>
        )}

        {/* Darstellung */}
        {section === 'darstellung' && (
          <Card dark={dark} style={{ padding: d.cardPad }}>
            <SectionTitle dark={dark} title={tr('settings.appearance.title')} subtitle={tr('settings.appearance.subtitle')} />

            <Field label={tr('settings.appearance.scheme')}>
              <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
                {([
                  { k: false, l: tr('settings.appearance.light'), ic: 'Sun'  },
                  { k: true,  l: tr('settings.appearance.dark'),  ic: 'Moon' },
                ] as const).map(o => {
                  const Ic = Icon[o.ic];
                  const active = dark === o.k;
                  return (
                    <button
                      key={String(o.k)}
                      onClick={() => setDark(o.k)}
                      style={{
                        flex: 1, padding: 14, borderRadius: 10,
                        border: `2px solid ${active ? BRAND : t.border}`,
                        background: active ? BRAND_LIGHT : t.card,
                        color: active ? BRAND : t.text,
                        cursor: 'pointer', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', gap: 8, fontSize: 13, fontWeight: 500,
                        fontFamily: 'Outfit, sans-serif',
                      }}
                    >
                      <Ic c={active ? BRAND : t.muted} />{o.l}
                    </button>
                  );
                })}
              </div>
            </Field>

            <div style={{ marginTop: 20 }}>
              <Field label={tr('settings.appearance.density')}>
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  {([
                    { k: 'comfortable' as Density, l: tr('settings.appearance.comfortable') },
                    { k: 'compact'     as Density, l: tr('settings.appearance.compact')     },
                  ]).map(o => (
                    <button
                      key={o.k}
                      onClick={() => setDensity(o.k)}
                      style={{
                        flex: 1, padding: 10, borderRadius: 8,
                        border: `1px solid ${density === o.k ? BRAND : t.border}`,
                        background: density === o.k ? BRAND_LIGHT : t.card,
                        color: density === o.k ? BRAND : t.text,
                        cursor: 'pointer', fontSize: 12, fontWeight: 500,
                        fontFamily: 'Outfit, sans-serif',
                      }}
                    >
                      {o.l}
                    </button>
                  ))}
                </div>
              </Field>
            </div>

            <div style={{ marginTop: 20, padding: 14, background: t.cardMuted, border: `1px solid ${t.borderSoft}`, borderRadius: 10 }}>
              <div style={{ fontSize: 12, color: t.muted }}>
                {tr('settings.appearance.auto_saved')}
              </div>
            </div>
          </Card>
        )}

        {/* Benachrichtigungen — ans Benachrichtigungssystem angebunden (WP7) */}
        {section === 'benachrichtigungen' && (
          <NotificationSettings dark={dark} cardPad={d.cardPad} />
        )}

        {/* Sicherheit */}
        {section === 'sicherheit' && (
          <Card dark={dark} style={{ padding: d.cardPad }}>
            <SectionTitle dark={dark} title={tr('settings.security.title')} subtitle={tr('settings.security.subtitle')} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/*
                Bug A (feature/crm2-batch-13): dieses Formular hatte weder
                State/onChange auf den Inputs noch onClick auf dem Button — ein
                Mockup, das beim Absenden STILL nichts tat. Jetzt echt
                angebunden an PUT /v2/admin/me/password.
              */}
              <PasswordChangeForm dark={dark} isMobile={isMobile} />

              <MfaMethodsManager dark={dark} />

              <div style={{
                padding: 14, background: t.cardMuted, border: `1px solid ${t.borderSoft}`,
                borderRadius: 10,
              }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: t.heading, marginBottom: 8 }}>{tr('settings.security.sessions')}</div>
                {[
                  { device: 'Chrome · MacBook Pro', location: 'München, DE', time: tr('settings.security.session_now') },
                  { device: 'Safari · iPhone',       location: 'Hamburg, DE',  time: tr('settings.security.session_2h') },
                ].map((s, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: i === 0 ? `1px solid ${t.borderSoft}` : 'none' }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 500, color: t.text }}>{s.device}</div>
                      <div style={{ fontSize: 11, color: t.muted, marginTop: 1 }}>{s.location} · {s.time}</div>
                    </div>
                    {i > 0 && <Button variant="secondary" size="sm" dark={dark}>{tr('settings.security.end_session')}</Button>}
                    {i === 0 && <Badge tone="success" dark={dark}>{tr('settings.security.this_session')}</Badge>}
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}

      </div>
    </div>
  );
}
